import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import moment from 'moment';
import { FC, useEffect, useMemo, useState } from 'react';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { ContactApi } from '../../../../../../services/api-client/contact-api';
import { TreatmentPlanModel } from '../../../../../../services/api-client/contact-api.types';
import { NoDataComponent } from '../../../../../shared/grid/no-data';
import { PatientProfileHeader } from '../../header/header';
import { QuickNavMenu } from '../../header/quick-nav-menu/quick-nav-menu';
import { DotSeparatedTitle, LiSeparator, List, ListItem } from '../../patient-profile-widget.styles';
import { ViewContent } from '../shared/view-content/view-content';
import { AutoFollowUpStatusComponent } from './auto-followup-status/auto-followup-status';
import { TreatmentPlanItemsDialog } from './edit-items-dialog/treatment-plan-items-dialog';
import { TreatmentPlanStatusComponent } from './treatment-plan-status';
import { ItemContent, ItemHeader } from './treatment-plan.styles';
import { TreatmentPlanSkeleton } from './treatment-plans.skeleton';

export const TreatmentPlanView: FC = () => {
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { showError } = useNotification();
  const { contactDetails, locationId } = usePatientProfile();
  const [treatmentPlans, setTreatmentPlans] = useState<TreatmentPlanModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TreatmentPlanModel>();
  const [dialogOpened, setDialogOpened] = useState(false);
  const contactApi = useMemo(() => ContactApi.initialize(config, oidcService), []);

  useEffect(() => {
    loadTreatmentPlans();
  }, []);

  const loadTreatmentPlans = async () => {
    setIsLoading(true);

    try {
      const treatmentPlans = await contactApi.getTreatmentPlans(contactDetails.id, locationId);
      setTreatmentPlans(treatmentPlans);
    } catch {
      showError('Unable to load treatment plans.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemClick = (plan: TreatmentPlanModel) => {
    setSelectedItem(plan);
    setDialogOpened(true);
  };

  const handleNoteDialogClose = async () => {
    setDialogOpened(false);
    setSelectedItem(undefined);
  };

  const handleTreatmentPlanUpdate = (plan: TreatmentPlanModel) => {
    setTreatmentPlans((prev) => prev.map((tp) => (tp.id === plan.id ? plan : tp)));
    setSelectedItem(plan);
  };

  return (
    <>
      <PatientProfileHeader />
      <QuickNavMenu />
      <ViewContent>
        {isLoading && <TreatmentPlanSkeleton />}
        {!isLoading && treatmentPlans.length === 0 && <NoDataComponent />}
        {!isLoading && treatmentPlans.length > 0 && (
          <>
            <List>
              {treatmentPlans.map((tp, index) => {
                return (
                  <>
                    <ListItem key={tp.id} onClick={() => handleItemClick(tp)}>
                      <ItemHeader>
                        <DotSeparatedTitle
                          leftTitle={tp.dateCreated ? moment(tp.dateCreated).format('MM/DD/YYYY') : null}
                          rightTitle={<TreatmentPlanStatusComponent status={tp.status} sx={{ fontWeight: 400 }} />}
                        />
                        <AutoFollowUpStatusComponent autoFollowUpStatus={tp.autoFollowUpStatus} />
                      </ItemHeader>
                      <ItemContent>{tp.description}</ItemContent>
                    </ListItem>
                    {index !== treatmentPlans.length - 1 && <LiSeparator />}
                  </>
                );
              })}
            </List>
          </>
        )}
      </ViewContent>
      {selectedItem && (
        <TreatmentPlanItemsDialog
          isOpen={dialogOpened}
          treatmentPlan={selectedItem}
          onTreatmentPlanUpdate={handleTreatmentPlanUpdate}
          onClose={handleNoteDialogClose}
        />
      )}
    </>
  );
};
